import { useState, useRef, useCallback, useMemo } from 'react';

const OTP_LENGTH = 6;
const EMPTY_VALUES = Array<string>(OTP_LENGTH).fill('');

const DECIMAL_DIGIT = /\p{Nd}/u;

// Unicode keeps each script's digits 0 to 9 in one run of code points, so a
// digit's value is its distance from the start of its run.
const asciiDigit = (digit: string) => {
  const point = digit.codePointAt(0)!;
  let zero = point;
  while (point - zero < 9 && DECIMAL_DIGIT.test(String.fromCodePoint(zero - 1))) {
    zero -= 1;
  }
  return String(point - zero);
};

// \d matches only 0-9, and the backend takes only those: a desktop IME in
// full-width mode types '４' (NFKC folds it), and a phone keypad in Arabic,
// Persian or Hindi types '٤', '۴' or '४' (mapped to their value).
export const otpDigits = (text: string) =>
  text.normalize('NFKC').replace(/\p{Nd}/gu, asciiDigit).replace(/\D/g, '');

export function useOtpInput() {
  const [values, setValues] = useState<string[]>(EMPTY_VALUES);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);

  const handleChange = useCallback((index: number, value: string) => {
    const digits = otpDigits(value);

    // SMS autofill may insert the full code into a single input
    if (digits.length > 1) {
      const next = [...EMPTY_VALUES];
      for (let i = 0; i < OTP_LENGTH; i++) next[i] = digits[i] || '';
      setValues(next);
      inputRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
      return;
    }

    const digit = digits.slice(-1);
    setValues((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Backspace' && !valuesRef.current[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [],
  );

  const handleFocus = useCallback((index: number) => {
    inputRefs.current[index]?.select();
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = otpDigits(e.clipboardData.getData('text')).slice(0, OTP_LENGTH);
    if (!pasted.length) return;
    const next = Array<string>(OTP_LENGTH).fill('');
    for (let i = 0; i < OTP_LENGTH; i++) {
      next[i] = pasted[i] || '';
    }
    setValues(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }, []);

  const reset = useCallback(() => {
    setValues(EMPTY_VALUES);
  }, []);

  const focusFirst = useCallback(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return useMemo(
    () => ({
      values,
      combined: values.join(''),
      inputRefs,
      handleChange,
      handleKeyDown,
      handleFocus,
      handlePaste,
      reset,
      focusFirst,
    }),
    [values, handleChange, handleKeyDown, handleFocus, handlePaste, reset, focusFirst],
  );
}
